import React, { FC, useCallback, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form';
import { useUncontrolledInput } from 'modules/core/hooks';
import { PROBABILITY_ESTIMATOR_FIELDS } from 'modules/ProbabilityEstimator/probabilityEstimator.const';
import {
  AdjustMenu,
  AdjustMenuItem,
  AdjustMenuItemTitle,
  AdjustToggle,
} from 'modules/core/components/Table/AdjustDropdown/styles';
import { PermissionWrapper } from 'modules/PermissionWrapper';
import { PERMISSIONS } from 'modules/PermissionWrapper/permissions.const';

import { VEHICLE_TABLE_VIEW_TITLES } from './options.const';
import { useVehicleTableViewOptions } from './useVehicleTableViewOptions';

interface IVehicleTableView {
  hiddenColumnsCallback: VoidFunction;
  onTableViewChange: VoidFunction;
}

export const VehicleTableView: FC<IVehicleTableView> = ({ hiddenColumnsCallback, onTableViewChange }) => {
  const { controlField } = useUncontrolledInput(PROBABILITY_ESTIMATOR_FIELDS.TABLE_VIEW);
  const { watch } = useFormContext();
  const selectedTableView = watch(PROBABILITY_ESTIMATOR_FIELDS.TABLE_VIEW);
  const vehicleTableViewOptions = useVehicleTableViewOptions();

  const dropdownTitle = useMemo(() => vehicleTableViewOptions.find(({ value }) => value === selectedTableView)?.title, [
    selectedTableView,
    vehicleTableViewOptions,
  ]);

  const handleAdjustMenuItemClick = useCallback(
    (onChange, value) => {
      onChange(value);
      hiddenColumnsCallback();
      onTableViewChange();
    },
    [onTableViewChange, hiddenColumnsCallback],
  );

  const renderOptions = useCallback(
    ({ onChange }: ControllerRenderProps) =>
      vehicleTableViewOptions.map(({ title, value, allowedRoles }) => (
        <PermissionWrapper allowedRoles={allowedRoles}>
          <AdjustMenuItem key={value} onClick={() => handleAdjustMenuItemClick(onChange, value)}>
            <AdjustMenuItemTitle>{title}</AdjustMenuItemTitle>
          </AdjustMenuItem>
        </PermissionWrapper>
      )),
    [handleAdjustMenuItemClick, vehicleTableViewOptions],
  );

  return (
    <Controller
      name={PROBABILITY_ESTIMATOR_FIELDS.TABLE_VIEW}
      controlField={controlField}
      render={(renderProps) => (
        <PermissionWrapper allowedRoles={PERMISSIONS.TABLE_VIEW_DROPDOWN}>
          <Dropdown>
            <AdjustToggle>{dropdownTitle || VEHICLE_TABLE_VIEW_TITLES.MANAGER}</AdjustToggle>
            <AdjustMenu>{renderOptions(renderProps)}</AdjustMenu>
          </Dropdown>
        </PermissionWrapper>
      )}
    />
  );
};
